import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppShell as CkAppShell,
  Banner,
  Button,
  CommandPalette,
  IconButton,
  Menu,
  MessagesCenterNext,
  Popover,
  PopoverBody,
  PopoverHeader,
  Sidebar,
  Spinner,
  Topbar,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkMenuItem, CkNavGroup, CkNavItem, CkQuickAction } from "@NP-Webber/crm-kit/dist/next/index.js";
import { useAuth } from "../auth/AuthProvider";
import { useEntityLabels } from "../i18n/EntityLabelsProvider";
import { shellApi, type NavItem } from "../api/shellApi";
import { ChatProvider } from "../modules/chat/ChatProvider";
import { FloatingChat } from "../modules/chat/FloatingChat";
import { useMessageCenter } from "../modules/messages/useMessageCenter";
import { getRealtimeSocket } from "../realtime/socketService";
import { formatOrganizationRoleLine } from "../modules/organizations/orgDisplay";
import { useDesign } from "../next/design/useDesign";
import { DesignOptions } from "../next/design/DesignOptions";
import { EVENTS, onEvent, openDesignTour, openHelpCenter } from "../next/events";
import { runQuickCreate, type QuickCreateType } from "../next/quickCreate";
import { DataEntryLauncherHost } from "../next/ai/dataEntry/DataEntryLauncherHost";
import { openDataEntryAgent } from "../next/ai/dataEntry/dataEntryLauncher";
import { ShellContext, type ShellContextValue } from "../next/shell/ShellContext";
import {
  buildModuleNav,
  currentNavItem,
  flattenNav,
  NAV_ICONS,
  NAV_MODULE_IDS,
  NAV_TOP_IDS,
  navModuleOf,
  OFFICE_IDS,
  PERSONAL_ID,
  SETTINGS_IDS,
  type FlatNavItem,
  type NavModuleKey,
} from "../next/shell/navConfig";
import { RelatedSettings, RelatedSettingsButton } from "../next/related/RelatedSettings";
import { HelpCenter } from "../next/help/HelpCenter";
import { DesignTour } from "../next/DesignTour";
import { TodayButton, TodayPanel, setTodayUser } from "../next/today/TodayPanel";
import { AutomationsButton, AutomationsPanel } from "../next/automations/AutomationsPanel";
import { buildPaletteResults } from "../next/search/paletteResults";
import { AssistantButton, AssistantPanel, loadAssistantConfigured } from "../next/assistant/AssistantPanel";

const ACCENT = "#4F46E5";
const PINS_KEY = "tb-next-qa-pins";
/** the module the user was last in — screens outside the modules (home, office, settings) keep it */
const MODULE_KEY = "tb-next-module";

/** Global "חדש" actions — each needs its screen in the menu + the create permission. */
const QUICK_ACTIONS: Array<{ type: QuickCreateType; label: string; icon: string; tone: CkQuickAction["tone"]; nav: string; perm: string }> = [
  { type: "customer", label: "לקוח", icon: "users", tone: "info", nav: "customers", perm: "customers.create" },
  { type: "task", label: "משימה", icon: "task", tone: "warning", nav: "tasks", perm: "tasks.create" },
  { type: "income", label: "הכנסה", icon: "wallet", tone: "success", nav: "income_expenses", perm: "incomes.create" },
  { type: "expense", label: "הוצאה", icon: "wallet", tone: "danger", nav: "income_expenses", perm: "expenses.cash.create" },
  { type: "meeting", label: "ישיבת צוות", icon: "calendar", tone: "violet", nav: "meetings", perm: "meetings.manage" },
  { type: "form", label: "טופס", icon: "note", tone: "primary", nav: "forms", perm: "forms.manage" },
  { type: "data_audit", label: "בדיקת נתונים (AI)", icon: "sparkle", tone: "violet", nav: "ai_data_audit", perm: "ai.data_audit.use" },
  { type: "user", label: "משתמש", icon: "user", tone: "neutral", nav: "users", perm: "users.create" },
  { type: "automation", label: "אוטומציה", icon: "sparkle", tone: "violet", nav: "automations", perm: "automations.manage" },
  { type: "mailing", label: "דיוור", icon: "mail", tone: "primary", nav: "mailing", perm: "mailing.manage" },
];

function readModule(): NavModuleKey {
  try {
    return sessionStorage.getItem(MODULE_KEY) === "finance" ? "finance" : "sales";
  } catch {
    return "sales";
  }
}

function readPins(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(PINS_KEY) || "null") as string[] | null;
    return Array.isArray(raw) ? raw : ["customer", "task", "income"];
  } catch {
    return ["customer", "task", "income"];
  }
}

/**
 * AppShell — the new design (crm-kit/next) chrome for every signed-in screen:
 * top-bar modules «מכירות» / «כספים» (like Turbo — each with its own sidebar groups and colour),
 * sidebar (grouped menu, "משרד", settings hub, personal menu, messages), top bar (Ctrl+K search,
 * quick create, related settings, help, notifications, automations, AI assistant, Today) and the global panels.
 */
export function AppShell() {
  const auth = useAuth();
  const labels = useEntityLabels();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useCkToast();
  const design = useDesign();
  const mc = useMessageCenter();
  const [navItems, setNavItems] = useState<NavItem[] | null>(null);
  const [granted, setGranted] = useState<Set<string>>(() => new Set());
  const [updateBanner, setUpdateBanner] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState("");
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [pinned, setPinned] = useState<string[]>(readPins);
  const [lastModule, setLastModule] = useState<NavModuleKey>(readModule);

  const displayName = auth.user?.displayName?.trim() || auth.user?.email || "משתמש";
  const organizationName = auth.activeTenant?.name?.trim() ?? "";
  const isSuperAdmin = auth.user?.platformRoles?.includes("super_admin") === true;
  const roleLabel = formatOrganizationRoleLine({ isSuperAdmin, code: auth.activeTenant?.code, name: organizationName });
  const tenantId = auth.activeTenant?.tenantId;

  useEffect(() => setTodayUser(auth.user?.id ?? null, displayName), [auth.user?.id, displayName]);

  useEffect(() => {
    void shellApi
      .listNavigation()
      .then(setNavItems)
      .catch(() => setNavItems([]));
    void shellApi
      .listGrantedPermissions()
      .then((keys) => setGranted(new Set(keys)))
      .catch(() => setGranted(new Set()));
  }, [tenantId]);

  useEffect(() => {
    const socket = getRealtimeSocket();
    const onVersion = (payload: { date?: string }) => {
      if (!payload?.date) return;
      if (localStorage.getItem("tb_app_version_snooze") === payload.date) return;
      setUpdateBanner(payload.date);
    };
    socket.on("appVersion", onVersion);
    return () => {
      socket.off("appVersion", onVersion);
    };
  }, []);

  useEffect(() => setNavOpen(false), [location.pathname]);
  useEffect(() => {
    try {
      localStorage.setItem(PINS_KEY, JSON.stringify(pinned));
    } catch {
      /* ignore */
    }
  }, [pinned]);
  useEffect(
    () =>
      onEvent<{ query?: string }>(EVENTS.palette, (d) => {
        setPaletteQuery(d?.query ?? "");
        setPaletteOpen(true);
      }),
    [],
  );

  /* ── menu ── */
  const nav: FlatNavItem[] = useMemo(() => flattenNav(navItems ?? [], labels.customers), [navItems, labels.customers]);
  const byKey = useMemo(() => new Map(nav.map((n) => [n.key, n])), [nav]);
  const current = useMemo(() => currentNavItem(location.pathname, nav), [location.pathname, nav]);
  const can = useCallback((key: string) => byKey.has(key), [byKey]);
  const allowed = useCallback((key: string) => granted.has(key), [granted]);
  const shellValue: ShellContextValue = useMemo(() => ({ nav, current, can, allowed }), [nav, current, can, allowed]);

  const toItem = useCallback(
    (it: FlatNavItem): CkNavItem => ({
      key: it.key,
      label: it.label,
      icon: NAV_ICONS[it.key] || NAV_ICONS[it.parentKey ?? ""] || "list",
      href: it.path,
      active: current?.key === it.key,
    }),
    [current],
  );

  /* ── modules (top bar) — the current screen picks its module; shared screens keep the last one ── */
  const modules = useMemo(() => buildModuleNav(byKey, labels.customers), [byKey, labels.customers]);
  const routeModule = navModuleOf(current?.key);
  const activeModule = modules.find((m) => m.key === (routeModule ?? lastModule)) ?? modules[0] ?? null;
  const rememberModule = useCallback((key: NavModuleKey) => {
    setLastModule(key);
    try {
      sessionStorage.setItem(MODULE_KEY, key);
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    if (routeModule && routeModule !== lastModule) rememberModule(routeModule);
  }, [routeModule, lastModule, rememberModule]);
  const goModule = useCallback(
    (key: string) => {
      const m = modules.find((x) => x.key === key);
      if (!m) return;
      rememberModule(m.key);
      navigate(m.homePath);
    },
    [modules, rememberModule, navigate],
  );
  const accent = activeModule?.color ?? ACCENT;

  const placed = new Set<string>([...NAV_MODULE_IDS, ...OFFICE_IDS, ...SETTINGS_IDS, PERSONAL_ID]);
  const topItems = NAV_TOP_IDS.map((id) => byKey.get(id)).filter((x): x is FlatNavItem => Boolean(x)).map(toItem);
  const groups: CkNavGroup[] = [
    { key: "g-top", items: topItems },
    ...(activeModule?.groups ?? []).map((g) => ({ key: g.key, title: g.title, items: g.items.map(toItem) })),
  ].filter((g) => g.items.length);
  const extra = nav.filter((n) => !placed.has(n.key)).map(toItem);
  if (extra.length) groups.push({ key: "g-more", title: "עוד", items: extra });
  const officeItems = OFFICE_IDS.map((id) => byKey.get(id)).filter((x): x is FlatNavItem => Boolean(x)).map(toItem);
  const settingsLinks = SETTINGS_IDS.map((id) => byKey.get(id)).filter((x): x is FlatNavItem => Boolean(x));
  const personal = byKey.get(PERSONAL_ID);

  /* ── quick actions ── */
  const canDataEntry = byKey.has("ai_data_entry") && (granted.has("ai.data_entry.use") || isSuperAdmin);
  const quickActions: CkQuickAction[] = useMemo(
    () => {
      const list: CkQuickAction[] = QUICK_ACTIONS.filter((a) => byKey.has(a.nav) && (granted.has(a.perm) || isSuperAdmin)).map((a) => ({
        key: a.type,
        label: a.label,
        icon: a.icon,
        tone: a.tone,
        onClick: () => runQuickCreate(a.type, navigate),
      }));
      // «הזנה חכמה» opens as a sheet over the current screen — Alt+6 like Turbo (ADR-0037)
      if (canDataEntry) list.splice(Math.min(5, list.length), 0, { key: "data_entry", label: "הזנה חכמה", icon: "upload", tone: "violet", onClick: () => openDataEntryAgent() });
      return list.map((a, i) => ({ ...a, kbd: i < 9 ? `Alt+${i + 1}` : undefined }));
    },
    [byKey, granted, isSuperAdmin, navigate, canDataEntry],
  );

  const handleLogout = useCallback(async () => {
    await auth.logout();
    navigate("/login", { replace: true });
  }, [auth, navigate]);

  const mayUseAssistant = granted.has("ai.use");
  const [assistantReady, setAssistantReady] = useState(false);
  useEffect(() => {
    if (!mayUseAssistant) return undefined;
    let alive = true;
    void loadAssistantConfigured().then((v) => alive && setAssistantReady(v));
    return () => {
      alive = false;
    };
  }, [mayUseAssistant]);

  const getResults = useCallback(
    (q: string) =>
      buildPaletteResults({
        query: q,
        nav,
        quickActions,
        customersLabel: labels.customers,
        can,
        assistant: mayUseAssistant && assistantReady,
        navigate,
      }),
    [nav, quickActions, labels.customers, can, mayUseAssistant, assistantReady, navigate],
  );

  /* ── personal menu ── */
  const modeItems = [
    { key: "light", label: "בהיר", icon: "sun" },
    { key: "dark", label: "כהה", icon: "moon" },
    { key: "system", label: "לפי המחשב", icon: "sliders" },
  ] as const;
  const userMenu: CkMenuItem[] = [
    { title: displayName },
    personal ? { key: "personal", label: "אזור אישי", icon: "user", onClick: () => navigate(personal.path) } : null,
    auth.memberships.length > 1 ? { key: "switch", label: "החלפת ארגון", icon: "swap", onClick: () => navigate("/select-tenant") } : null,
    { divider: true },
    { title: "מצב תצוגה" },
    ...modeItems.map((m) => ({
      key: `mode-${m.key}`,
      label: m.label,
      icon: m.icon,
      active: design.mode === m.key,
      end: design.mode === m.key ? "✓" : null,
      onClick: () => design.setMode(m.key),
    })),
    { divider: true },
    { key: "help", label: "מרכז הדרכה", icon: "help", end: "F1", onClick: () => openHelpCenter() },
    { key: "tour", label: "סיור בעיצוב", icon: "sparkle", onClick: () => openDesignTour() },
    { key: "logout", label: "התנתקות", icon: "logout", danger: true, onClick: () => void handleLogout() },
  ].filter(Boolean) as CkMenuItem[];

  const bottomItems: CkNavItem[] = settingsLinks.length
    ? [
        {
          key: "__settings",
          label: "הגדרות",
          icon: "gear",
          onClick: (e) => setSettingsAnchor(e.currentTarget as HTMLElement),
          active: settingsLinks.some((s) => current?.key === s.key),
        },
      ]
    : [];

  const sidebar = (
    <Sidebar
      org={{ name: organizationName || "טורבו ביזנעס", sub: "טורבו ביזנעס", logoText: "T" }}
      groups={groups}
      fixedGroups={officeItems.length ? [{ key: "office", title: "משרד", items: officeItems }] : []}
      bottomItems={bottomItems}
      user={{ name: displayName, role: roleLabel }}
      userMenu={userMenu}
      footerActions={[
        { key: "messages", icon: "note", label: "מרכז הודעות", dot: mc.unreadCount > 0, onClick: () => mc.setOpen(true) },
      ]}
      onNavigate={(it) => {
        if (it.href) navigate(String(it.href));
      }}
    />
  );

  const topbar = (
    <Topbar
      onMenu={() => setNavOpen(true)}
      modules={modules.length > 1 ? modules.map((m) => ({ key: m.key, label: m.label, color: m.color })) : undefined}
      activeModule={activeModule?.key}
      onModuleChange={goModule}
      onSearch={() => {
        setPaletteQuery("");
        setPaletteOpen(true);
      }}
      searchLabel="חיפוש בכל המערכת"
      quickActions={quickActions}
      pinnedActions={pinned.filter((k) => quickActions.some((a) => a.key === k))}
      onPinnedChange={setPinned}
      onPinLimit={(n) => toast(`אפשר להצמיד עד ${n} קיצורים`)}
      end={
        <>
          <RelatedSettingsButton />
          <IconButton icon="help" label="עזרה ומדריכים למסך הזה (F1)" onClick={() => openHelpCenter()} />
          <IconButton icon="bell" label="מרכז הודעות" badge={mc.unreadCount || undefined} onClick={() => mc.setOpen(true)} />
          <AutomationsButton />
          <AssistantButton />
          {/* last in the end slot = the top-left corner (RTL) */}
          <TodayButton />
        </>
      }
    />
  );

  return (
    <ShellContext.Provider value={shellValue}>
      <ChatProvider>
        <HelpCenter onReportBug={() => mc.setOpen(true)}>
          {/* the module colour flows to the sidebar active marker / logo via --ck-accent */}
          <div style={{ ["--ck-accent" as string]: accent, display: "contents" }}>
            <CkAppShell sidebar={sidebar} topbar={topbar} sidebarOpen={navOpen} onSidebarClose={() => setNavOpen(false)}>
              {updateBanner ? (
                <div className="tb-notice">
                  <Banner
                    tone="info"
                    icon="refresh"
                    action={
                      <>
                        <Button size="sm" variant="primary" onClick={() => window.location.reload()}>
                          רענון עכשיו
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            localStorage.setItem("tb_app_version_snooze", updateBanner);
                            setUpdateBanner(null);
                          }}
                        >
                          אחר כך
                        </Button>
                      </>
                    }
                  >
                    יש עדכון חדש זמין באתר
                  </Banner>
                </div>
              ) : null}
              {mc.personalUpdateSnack ? (
                <div className="tb-notice">
                  <Banner
                    tone="success"
                    icon="bell"
                    onClose={() => mc.setPersonalUpdateSnack(false)}
                    action={
                      <Button
                        size="sm"
                        onClick={() => {
                          mc.setPersonalUpdateSnack(false);
                          mc.setOpen(true);
                        }}
                      >
                        פתיחה
                      </Button>
                    }
                  >
                    יש עדכון בדיווח או בהצעה שלך
                  </Banner>
                </div>
              ) : null}
              <Suspense
                fallback={
                  <div className="ck-empty">
                    <Spinner size={28} />
                  </div>
                }
              >
                {navItems === null ? (
                  <div className="ck-empty" style={{ minHeight: "40vh" }}>
                    <Spinner size={28} />
                  </div>
                ) : (
                  <Outlet />
                )}
              </Suspense>
            </CkAppShell>
          </div>

          <CommandPalette
            open={paletteOpen}
            initialQuery={paletteQuery}
            onClose={() => setPaletteOpen(false)}
            getResults={getResults}
            placeholder={`חיפוש ${labels.customer}, משתמש, משימה, מסך או הגדרה…`}
          />

          <Popover open={Boolean(settingsAnchor)} anchorEl={settingsAnchor} onClose={() => setSettingsAnchor(null)} width={300}>
            <PopoverHeader icon="gear" title="הגדרות" sub="כל ההגדרות במקום אחד" onClose={() => setSettingsAnchor(null)} />
            <Menu
              items={settingsLinks.map((l) => ({
                key: l.key,
                label: l.label,
                icon: NAV_ICONS[l.key] || "gear",
                onClick: () => {
                  setSettingsAnchor(null);
                  navigate(l.path);
                },
              }))}
            />
            <PopoverBody>
              <DesignOptions compact />
            </PopoverBody>
          </Popover>

          <DataEntryLauncherHost enabled={canDataEntry} />

          <MessagesCenterNext
            open={mc.open}
            onClose={() => mc.setOpen(false)}
            messages={mc.messages}
            loading={mc.loading}
            bugReports={mc.bugReports}
            reportsLoading={mc.reportsLoading}
            pendingSuggestions={mc.pendingSuggestions}
            pendingSuggestionsLoading={mc.pendingSuggestionsLoading}
            isSuperAdmin={mc.isAdmin}
            currentUserId={mc.currentUserId}
            canSuggest={mc.canSuggest}
            canVoteOn={(message: unknown) => mc.canVoteOn(message as Parameters<typeof mc.canVoteOn>[0])}
            onMarkSeen={mc.handleMarkSeen}
            onVote={mc.handleVote}
            onSubmitReport={mc.handleSubmitReport}
            onSubmitSuggestion={mc.handleSubmitSuggestion}
            onSubmitSystemMessage={mc.handleSubmitSystemMessage}
            onReportStatusChange={(reportId: string, status: string) =>
              mc.handleReportStatusChange(reportId, status as "open" | "in_progress" | "resolved")
            }
            onReportUpdate={(reportId: string, patch: { status?: string; adminNote?: string }) =>
              mc.handleReportUpdate(reportId, {
                status: patch.status as "open" | "in_progress" | "resolved" | undefined,
                adminNote: patch.adminNote,
              })
            }
            onDeleteBugReport={mc.handleDeleteBugReport}
            onApproveSuggestion={mc.handleApproveSuggestion}
            onRejectSuggestion={mc.handleRejectSuggestion}
            onHandleSuggestion={mc.handleHandleSuggestion}
            onDeleteSuggestion={mc.handleDeleteSuggestion}
            onMessageAction={(message: unknown) => mc.handleMessageAction(message as Parameters<typeof mc.handleMessageAction>[0])}
            audienceOptions={mc.audienceOptions}
            voteRoleOptions={mc.voteRoleOptions}
          />

          <RelatedSettings />
          <TodayPanel />
          <AutomationsPanel />
          <AssistantPanel />
          <DesignTour userId={auth.user?.id ?? null} />
          <FloatingChat />
        </HelpCenter>
      </ChatProvider>
    </ShellContext.Provider>
  );
}

export default AppShell;
